import { type ShareableResource } from "@/contexts/ShareResourceContext";
import { getPrincipal } from "@/principal";
import { useGetAccount } from "@/queries/account-queries";
import {
    useCreateRoleAssignment,
    useDeleteRoleAssignment,
    useListGroups,
    useListRoleAssignments,
} from "@/queries/group-queries";
import { useCreateProjectPolicy, useDeleteProjectPolicy, useProjectState } from "@/queries/project-queries";
import { useListRunnerPolicies, useUpdateSharingSetting } from "@/queries/runner-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { ProjectRole } from "gitpod-next-api/gitpod/v1/project_pb";
import { useCallback, useMemo } from "react";

const GroupNames = {
    OrgMembers: "org-members",
};

type UseResourceSharingReturn = {
    isShared: boolean;
    share: () => Promise<void>;
    unshare: () => Promise<void>;
    isPending: boolean;
    orgName: string;
};

export const useResourceSharing = (resource: ShareableResource): UseResourceSharingReturn => {
    const { data: account } = useGetAccount();

    // Project-specific hooks
    const { data: projectState } = useProjectState(
        resource.resourceType === ResourceType.PROJECT ? resource.resourceId : undefined,
    );
    const createProjectPolicy = useCreateProjectPolicy();
    const deleteProjectPolicy = useDeleteProjectPolicy();

    // Runner-specific hooks
    const { data: runnerPolicies } = useListRunnerPolicies(
        resource.resourceType === ResourceType.RUNNER ? resource.resourceId : "",
    );
    const { data: groups } = useListGroups();
    const updateSharingSetting = useUpdateSharingSetting(
        resource.resourceType === ResourceType.RUNNER ? resource.resourceId : "",
    );

    const principal = getPrincipal();
    const membership = principal ? account?.memberships?.find((m) => m.userId === principal) : undefined;
    const orgName = membership?.organizationName || "your Organization";

    // Compute org-members group (needed before useListRoleAssignments for groupId filter)
    const orgMembersGroup = useMemo(() => {
        if (!membership || !groups) {
            return undefined;
        }
        return groups.groups.find(
            (g) => g.organizationId === membership.organizationId && g.name === GroupNames.OrgMembers,
        );
    }, [membership, groups]);

    // Workflow-specific hooks — filter by org-members groupId so the backend
    // returns system-managed group assignments on all tiers.
    const { data: workflowAssignments } = useListRoleAssignments({
        resourceType: ResourceType.WORKFLOW,
        resourceId: resource.resourceType === ResourceType.WORKFLOW ? resource.resourceId : undefined,
        groupId: orgMembersGroup?.id,
        enabled: resource.resourceType === ResourceType.WORKFLOW && !!orgMembersGroup,
    });
    const createRoleAssignment = useCreateRoleAssignment();
    const deleteRoleAssignment = useDeleteRoleAssignment();

    // Compute isShared based on resource type
    const isShared = useMemo(() => {
        if (resource.resourceType === ResourceType.PROJECT) {
            return projectState?.shared ?? false;
        } else if (resource.resourceType === ResourceType.RUNNER) {
            if (!runnerPolicies || !orgMembersGroup) {
                return false;
            }
            return runnerPolicies.policies.some((p) => p.groupId === orgMembersGroup.id);
        } else if (resource.resourceType === ResourceType.WORKFLOW) {
            if (!workflowAssignments || !orgMembersGroup) {
                return false;
            }
            return workflowAssignments.assignments.some((a) => a.groupId === orgMembersGroup.id);
        }
        return false;
    }, [resource.resourceType, projectState, runnerPolicies, workflowAssignments, orgMembersGroup]);

    // Share action
    const share = useCallback(async (): Promise<void> => {
        if (resource.resourceType === ResourceType.PROJECT) {
            if (!projectState) {
                throw new Error("Project state not loaded");
            }
            await createProjectPolicy.mutateAsync({
                projectId: resource.resourceId,
                groupId: projectState.orgMembersGroupId,
                role: ProjectRole.USER,
            });
        } else if (resource.resourceType === ResourceType.RUNNER) {
            if (!orgMembersGroup) {
                throw new Error("Organization members group not found");
            }
            await updateSharingSetting.mutateAsync({
                setting: "anyone-in-org",
                groupId: orgMembersGroup.id,
            });
        } else if (resource.resourceType === ResourceType.WORKFLOW) {
            if (!orgMembersGroup) {
                throw new Error("Organization members group not found");
            }
            await createRoleAssignment.mutateAsync({
                groupId: orgMembersGroup.id,
                resourceType: ResourceType.WORKFLOW,
                resourceId: resource.resourceId,
                resourceRole: ResourceRole.WORKFLOW_EXECUTOR,
            });
        }
    }, [resource, projectState, orgMembersGroup, createProjectPolicy, updateSharingSetting, createRoleAssignment]);

    // Unshare action
    const unshare = useCallback(async (): Promise<void> => {
        if (resource.resourceType === ResourceType.PROJECT) {
            if (!projectState) {
                throw new Error("Project state not loaded");
            }
            await deleteProjectPolicy.mutateAsync({
                projectId: resource.resourceId,
                groupId: projectState.orgMembersGroupId,
            });
        } else if (resource.resourceType === ResourceType.RUNNER) {
            if (!orgMembersGroup) {
                throw new Error("Organization members group not found");
            }
            await updateSharingSetting.mutateAsync({
                setting: "only-me",
                groupId: orgMembersGroup.id,
            });
        } else if (resource.resourceType === ResourceType.WORKFLOW) {
            if (!orgMembersGroup) {
                throw new Error("Organization members group not found");
            }
            const assignment = workflowAssignments?.assignments.find((a) => a.groupId === orgMembersGroup.id);
            if (assignment) {
                await deleteRoleAssignment.mutateAsync({
                    assignmentId: assignment.id,
                    resourceType: ResourceType.WORKFLOW,
                    resourceId: resource.resourceId,
                });
            }
        }
    }, [
        resource,
        projectState,
        orgMembersGroup,
        deleteProjectPolicy,
        updateSharingSetting,
        workflowAssignments,
        deleteRoleAssignment,
    ]);

    const isPending = useMemo(() => {
        if (resource.resourceType === ResourceType.PROJECT) {
            return createProjectPolicy.isPending || deleteProjectPolicy.isPending;
        } else if (resource.resourceType === ResourceType.RUNNER) {
            return updateSharingSetting.isPending;
        } else if (resource.resourceType === ResourceType.WORKFLOW) {
            return createRoleAssignment.isPending || deleteRoleAssignment.isPending;
        }
        return false;
    }, [
        resource.resourceType,
        createProjectPolicy.isPending,
        deleteProjectPolicy.isPending,
        updateSharingSetting.isPending,
        createRoleAssignment.isPending,
        deleteRoleAssignment.isPending,
    ]);

    return {
        isShared,
        share,
        unshare,
        isPending,
        orgName,
    };
};
